import React, { useState } from 'react';
import { Col, Row } from '../../lib/utils';
import { colors } from '../../lib/colors';
import { Body2Regular } from '../../lib/font';
import Modal from '../../components/modal/Modal';
import ColorSquareButton from '../../components/Button/ColorSquareButton';
import WhiteSquareButton from '../../components/Button/WhiteSquareButton';
import { useCreateTicket } from '@/features/groupTicket/hooks';
import { CreateTicketBody, ExpiryPolicy } from '@/features/groupTicket/types';

const CLASS_TYPE_OPTIONS = [
    { code: 'GROUP', label: '그룹' },
    { code: 'PRIVATE_1ON2', label: '프라이빗 1:2' },
    { code: 'PRIVATE_1ON3', label: '프라이빗 1:3' },
];

const EXPIRY_POLICY_OPTIONS: { value: ExpiryPolicy; label: string }[] = [
    { value: 'FIRST', label: '둘 중 빠른 것' },
    { value: 'COUNT', label: '회차 소진 시' },
    { value: 'PERIOD', label: '기간 만료 시' },
];

const inputStyle: React.CSSProperties = {
    border: '1px solid #E1E4E6',
    borderRadius: 8,
    padding: '10px 12px',
    fontSize: 14,
    width: '100%',
    outline: 'none',
};

const ModalRegisterGroupTicket = ({
    centerId,
    onClose,
    createAlert,
}: {
    centerId: string;
    onClose: () => void;
    createAlert: any;
}) => {
    const createMutation = useCreateTicket();

    const [name, setName] = useState('');
    const [expiryPolicy, setExpiryPolicy] = useState<ExpiryPolicy>('FIRST');
    const [totalCount, setTotalCount] = useState<number | undefined>(10);
    const [periodDays, setPeriodDays] = useState<number | undefined>(90);
    const [price, setPrice] = useState(0);
    const [allowedClassTypes, setAllowedClassTypes] = useState<string[]>(['GROUP']);
    const [cancellableCount, setCancellableCount] = useState<number | undefined>(undefined);
    const [perSessionFee, setPerSessionFee] = useState<number | undefined>(undefined);

    // 자동 계산: 가격 / 총 횟수 (PERIOD 타입은 자동 계산 불가 — 사용자 입력 필요)
    const autoPerSessionFee = expiryPolicy !== 'PERIOD' && price && totalCount && totalCount > 0
        ? Math.round(price / totalCount)
        : undefined;

    const toggleClassType = (code: string) => {
        setAllowedClassTypes((prev) =>
            prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
        );
    };

    const handleSubmit = async () => {
        if (!name.trim()) {
            createAlert('입력 오류', '상품명을 입력해주세요.', true);
            return;
        }
        if (allowedClassTypes.length === 0) {
            createAlert('입력 오류', '사용 가능 수업 유형을 선택해주세요.', true);
            return;
        }
        if ((expiryPolicy === 'COUNT' || expiryPolicy === 'FIRST') && (!totalCount || totalCount < 1)) {
            createAlert('입력 오류', '총 횟수를 입력해주세요.', true);
            return;
        }
        if ((expiryPolicy === 'PERIOD' || expiryPolicy === 'FIRST') && (!periodDays || periodDays < 1)) {
            createAlert('입력 오류', '유효 기간을 입력해주세요.', true);
            return;
        }
        if (expiryPolicy === 'PERIOD' && !perSessionFee) {
            createAlert('입력 필요', '기간제 수강권은 회당 가격을 직접 입력해주세요.', true);
            return;
        }

        const body: CreateTicketBody = {
            name: name.trim(),
            expiryPolicy,
            totalCount: totalCount || undefined,
            periodDays: periodDays || undefined,
            price,
            classType: (allowedClassTypes[0] || 'GROUP') as any,
            allowedClassTypes,
            cancellableCount: cancellableCount || undefined,
            perSessionFee: perSessionFee != null ? perSessionFee : autoPerSessionFee,
        };

        try {
            await createMutation.mutateAsync({ centerId, body });
            createAlert('등록 완료', '수강권 상품이 등록되었습니다.', false);
            onClose();
        } catch {
            createAlert('등록 실패', '수강권 등록에 실패했습니다.', true);
        }
    };

    return (
        <Modal isCloseButton setIsVisible={onClose} whiteViewWidth={680} isWhiteOverFlowScroll={false}>
            <Col style={{ maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
                {/* 헤더 (고정) */}
                <div style={{ padding: '32px 36px 0 36px', flexShrink: 0 }}>
                    <span style={{ fontSize: 20, fontWeight: 700, color: '#111827', display: 'block', marginBottom: 24 }}>
                        그룹수업 수강권 등록
                    </span>
                </div>

                {/* 스크롤 가능한 폼 영역 */}
                <div style={{ padding: '0 36px 20px 36px', overflowY: 'auto', flex: 1 }}>
                    <Col style={{ gap: 20 }}>
                    <Col style={{ gap: 6 }}>
                        <Body2Regular color={colors.LAVEL_2}>상품명</Body2Regular>
                        <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} placeholder="GX 10회권" />
                    </Col>

                    <Row style={{ gap: 12 }}>
                        <Col style={{ flex: 1, gap: 6 }}>
                            <Body2Regular color={colors.LAVEL_2}>총 횟수 (선택)</Body2Regular>
                            <input style={inputStyle} type="number" value={totalCount ?? ''} onChange={(e) => setTotalCount(e.target.value ? Number(e.target.value) : undefined)} placeholder="미설정" min={1} />
                        </Col>
                        <Col style={{ flex: 1, gap: 6 }}>
                            <Body2Regular color={colors.LAVEL_2}>유효 기간 (일, 선택)</Body2Regular>
                            <input style={inputStyle} type="number" value={periodDays ?? ''} onChange={(e) => setPeriodDays(e.target.value ? Number(e.target.value) : undefined)} placeholder="미설정" min={1} />
                        </Col>
                    </Row>

                    <Col style={{ gap: 6 }}>
                        <Body2Regular color={colors.LAVEL_2}>만료 정책</Body2Regular>
                        <Row style={{ gap: 6 }}>
                            {EXPIRY_POLICY_OPTIONS.map((opt) => {
                                const sel = expiryPolicy === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        onClick={() => setExpiryPolicy(opt.value)}
                                        style={{
                                            flex: 1, padding: '8px 0', borderRadius: 8, fontSize: 13,
                                            border: sel ? '1.5px solid #6366F1' : '1px solid #E1E4E6',
                                            backgroundColor: sel ? '#EEF2FF' : 'white',
                                            color: sel ? '#4338CA' : '#6B7280',
                                            fontWeight: sel ? 600 : 400, cursor: 'pointer',
                                        }}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </Row>
                    </Col>

                    <Row style={{ gap: 12 }}>
                        <Col style={{ flex: 1, gap: 6 }}>
                            <Body2Regular color={colors.LAVEL_2}>판매 금액 (원)</Body2Regular>
                            <input style={inputStyle} inputMode="numeric" value={price ? price.toLocaleString() : ''} onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); setPrice(v ? Number(v) : 0); }} placeholder="예: 300,000" />
                        </Col>
                        <Col style={{ flex: 1, gap: 6 }}>
                            <Body2Regular color={colors.LAVEL_2}>
                                회당 가격{expiryPolicy === 'PERIOD' && <span style={{ color: '#EF4444', marginLeft: 4 }}>*필수</span>}
                            </Body2Regular>
                            <input
                                style={inputStyle}
                                inputMode="numeric"
                                value={perSessionFee ? perSessionFee.toLocaleString() : ''}
                                onChange={(e) => {
                                    const v = e.target.value.replace(/[^0-9]/g, '');
                                    setPerSessionFee(v ? Number(v) : undefined);
                                }}
                                placeholder={autoPerSessionFee ? `자동: ${autoPerSessionFee.toLocaleString()}원` : '직접 입력'}
                            />
                            {autoPerSessionFee && !perSessionFee && (
                                <span style={{ fontSize: 11, color: '#9CA3AF' }}>미입력 시 자동 계산: {autoPerSessionFee.toLocaleString()}원 (판매금액/횟수)</span>
                            )}
                        </Col>
                    </Row>

                    <Row style={{ gap: 12 }}>
                        <Col style={{ flex: 1, gap: 6 }}>
                            <Body2Regular color={colors.LAVEL_2}>취소 허용 횟수 (선택)</Body2Regular>
                            <input
                                style={inputStyle}
                                type="number"
                                value={cancellableCount ?? ''}
                                onChange={(e) => setCancellableCount(e.target.value ? Number(e.target.value) : undefined)}
                                placeholder="미설정 = 무제한"
                                min={1}
                            />
                        </Col>
                        <Col style={{ flex: 1, gap: 6 }} />
                    </Row>

                    <Col style={{ gap: 6 }}>
                        <Body2Regular color={colors.LAVEL_2}>사용 가능 수업 유형</Body2Regular>
                        <Row style={{ gap: 6, flexWrap: 'wrap' }}>
                            {CLASS_TYPE_OPTIONS.map((opt) => {
                                const sel = allowedClassTypes.includes(opt.code);
                                return (
                                    <button
                                        key={opt.code}
                                        onClick={() => toggleClassType(opt.code)}
                                        style={{
                                            padding: '8px 16px', borderRadius: 8,
                                            border: sel ? '1.5px solid #10B981' : '1px solid #E1E4E6',
                                            backgroundColor: sel ? '#ECFDF5' : 'white',
                                            color: sel ? '#065F46' : '#6B7280',
                                            fontSize: 13, fontWeight: sel ? 600 : 400, cursor: 'pointer',
                                        }}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </Row>
                    </Col>
                    </Col>
                </div>

                {/* 푸터 (고정) */}
                <div style={{ padding: '20px 36px 28px 36px', flexShrink: 0, borderTop: '1px solid #F1F5F9' }}>
                    <Row style={{ gap: 12, justifyContent: 'flex-end' }}>
                        <WhiteSquareButton text="취소" size="middle" callBack={onClose} />
                        <ColorSquareButton text="등록" size="middle" callBack={handleSubmit} isDisabled={createMutation.isLoading} />
                    </Row>
                </div>
            </Col>
        </Modal>
    );
};

export default ModalRegisterGroupTicket;
